import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/motion/Reveal";
import { Users, Building2 } from "lucide-react";

export const faqs = [
  {
    q: "What is GateMate?",
    a: "GateMate is real-time networking for live events. When you check in to a concert, game, festival or lounge, you join that event's GateRoom — see who's in the room and connect with the people there right now. Photos from the night go to the morning-after album: everyone's shots unlock at 9am.",
    audience: "attendees",
  },
  {
    q: "Does GateMate sell tickets or list events?",
    a: "No — GateMate is not a ticketing or event-discovery app. We're the live social layer for people already at the same venue. Your venue keeps its ticketing; we make the room social.",
    audience: "attendees",
  },
  {
    q: "What's the difference between a GateRoom and the door QR?",
    a: "A GateRoom is the live in-app room — the people, the chat, the night. The door QR is what opens one: the printed code at the venue or the short code a friend texts you. Anyone can open a private GateRoom and share the code; public rooms on the map are run by partner venues.",
    audience: "attendees",
  },
  {
    q: "How is it different from other social apps?",
    a: "Most apps are built around an endless feed. GateMate is built around presence: it only matters when you're actually somewhere, with real people, at the same moment. No performance, no doomscroll — just the room you're in.",
    audience: "attendees",
  },
  {
    q: "Where is GateMate available?",
    a: "Everywhere — the app is live on iOS and Android worldwide, and any member can open a room with a code and share it tonight, in any city. Public venue rooms light up first in Dallas–Fort Worth, where we're signing founding venues now. Tell us your city and we'll bring partner venues there next.",
    audience: "attendees",
  },
  {
    q: "Is my data and privacy protected?",
    a: "Yes. GateRooms are ephemeral — they exist for the night and close when it ends (private rooms close themselves after 4 hours). One-on-one chats you start last forever, and you're never broadcast beyond the rooms you choose to step into.",
    audience: "attendees",
  },
  {
    q: "Is it free to join?",
    a: "Yes — joining rooms, chatting, saying hi and messaging your connections never costs anything, and never will. For bigger nights there's GateMate+: $4.99 a month, $39.99 a year, or a $2.99 Night Pass that covers one night and doesn't renew. Full breakdown on the Pricing page.",
    audience: "attendees",
  },
  {
    q: "How do GateMate invites work?",
    a: "Every GateRoom comes with its own invite: a 6-letter code you can read out loud, a QR that opens the room the second it's scanned, and a story-ready card you can post in Bold, Minimal or Photo style. Hosting? You can also drop a private room straight into the DMs of people you've already met on GateMate. No list to climb — anyone with the code or QR is in.",
    audience: "attendees",
  },
  {
    q: "Which phones does GateMate support?",
    a: "GateMate is live on iPhone and Android — get it on the App Store or Google Play. You can also use the GateMate web app from any modern browser at app.gatemate.net — all three connect to the same live rooms.",
    audience: "attendees",
  },
  {
    q: "Who can use GateMate?",
    a: "GateMate is an 18+ platform. Some rooms run 21+ nights — ID rules follow each venue, exactly like walking in without the app.",
    audience: "attendees",
  },
  {
    q: "Do you track my location?",
    a: "We never track you in the background and never build a history of where you've been. Your phone's location is read in a few explicit moments only: at first launch to check whether your city has live rooms, when you tap \"Rooms near me\", at the door if a host requires guests to actually be at the venue, and — only if you switch it on inside a festival room — Find Friends, which shows a mutual friend you've both chosen a distance and direction (\"about 300 ft NE\"), never a coordinate or a map pin. You can always join a room by typing its code without granting location at all.",
    audience: "attendees",
  },
  {
    q: "Will GateMate tell me what's on when my flight lands?",
    a: "Yes — save your flight in the app and when it lands you'll get one notification about what's happening in that airport's GateRoom: at most one per airport every six hours, and never while the room is closed. It runs off the airline's published arrival time, not your phone — no location permission, nothing watching you travel. Silence it anytime at Settings → Notifications → Travel.",
    audience: "attendees",
  },
  {
    q: "What if nobody else is at my venue yet?",
    a: "You'll be the first in the room. New arrivals show up automatically the second they check in, so drop a hello, save the link, and share it with a friend at the door — the room fills in minutes at any decent-sized event.",
    audience: "attendees",
  },
  {
    q: "Can I hide from a room without leaving?",
    a: "Yes. In Settings there's a Hide-from-rooms toggle. When it's on, you can see who else is in the room but you don't appear on their member lists. Flip it off when you want to be visible again.",
    audience: "attendees",
  },
  {
    q: "What happens to my guests' data?",
    a: "Guests own their own connections. We share aggregate patterns with your venue (check-ins, groups formed, return-with-squad rate), never individual profiles. Rooms are ephemeral, so the guest list fades gracefully after the event. Full detail lives in our Privacy Policy.",
    audience: "venues",
  },
  {
    q: "How long does venue setup actually take?",
    a: "About 15 minutes. We generate your venue's check-in code, send you a printable QR poster for the door, and share a private link to your live dashboard. No POS integration, no hardware install, no staff training. You could be live at the door tonight.",
    audience: "venues",
  },
  {
    q: "How do venues pay?",
    a: "They don't — Founding Venues are free while we open Dallas–Fort Worth; there's nothing to buy yet. If a paid venue tier ever launches, you'll hear the number long before we ask for it.",
    audience: "venues",
  },
  {
    q: "Are there other apps doing this?",
    a: "There are consumer apps for meeting people at events (Radiate, EventBuddie, Friendstival, ConcertBuddy) and POS analytics platforms for venues (SevenRooms, OpenTable Reporting, Bloom Intelligence). None of them do both — GateMate is the only layer that gives venues group-behavior data while giving attendees the actual social experience. Our full comparison is on the For Venues section.",
    audience: "venues",
  },
  {
    q: "What if it flops? Am I locked in?",
    a: "Zero contract. Zero commitment. If your door QR doesn't drive measurable impact after the first month, you turn it off and we part ways as friends. We prove value before you owe us anything.",
    audience: "venues",
  },
];

export default function FAQ() {
  const [tab, setTab] = useState("attendees");

  useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-faq", "true");
    s.text = JSON.stringify(jsonLd);
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  const visible = faqs.filter((f) => f.audience === tab);

  return (
    <section id="faq" className="relative py-24 md:py-32 overflow-hidden bg-[#0b1613]" data-testid="faq-section">
      <div className="container-x relative z-10 grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-4">
          <p className="overline mb-4">FAQ</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-none">
            Questions, <span className="text-gradient">answered</span>.
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            Everything you need to know before you step in — whether you're coming to a show or running the venue.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="md:col-span-8">
          {/* Audience tabs */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10 mb-8" data-testid="faq-tabs" role="tablist">
            <button
              onClick={() => setTab("attendees")}
              role="tab"
              aria-selected={tab === "attendees"}
              data-testid="faq-tab-attendees"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                tab === "attendees"
                  ? "bg-gradient-to-r from-neon-cyan/25 to-neon-violet/25 text-white ring-1 ring-neon-cyan/40"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Users className="w-4 h-4" /> For attendees
            </button>
            <button
              onClick={() => setTab("venues")}
              role="tab"
              aria-selected={tab === "venues"}
              data-testid="faq-tab-venues"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${
                tab === "venues"
                  ? "bg-gradient-to-r from-neon-cyan/25 to-neon-violet/25 text-white ring-1 ring-neon-cyan/40"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Building2 className="w-4 h-4" /> For venues
            </button>
          </div>

          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion" key={tab}>
            {visible.map((f, i) => (
              <AccordionItem key={`${tab}-${i}`} value={`item-${tab}-${i}`} className="border-b border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${tab}-${i}`} className="text-left font-display text-lg md:text-xl font-semibold text-white hover:text-neon-cyan hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                {/* forceMount keeps every answer in the DOM (visually hidden
                    when closed) so crawlers and link previews see the full FAQ. */}
                <AccordionContent forceMount className="text-zinc-400 text-base leading-relaxed pb-6 data-[state=closed]:hidden">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}
